import { useEffect, useState } from "react";
import { api, imgUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Lock, X } from "lucide-react";

const EMPTY = { name: "", kind: "lavoro", category: "", image: "", status: "", location: "", description: "", plans: [], sale_link: "" };

const inputCls = "mt-2 border-slate-300 bg-white text-slate-900 focus-visible:ring-gold";
const labelCls = "text-xs uppercase tracking-[0.2em] text-slate-500";

function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Benvenuto nell'Area Riservata");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[70vh] place-items-center px-5">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm border border-slate-200 bg-white p-8 shadow-sm"
      >
        <Lock className="h-6 w-6 text-gold" />
        <h1 className="mt-4 font-serif text-2xl text-slate-900 sm:text-3xl">Area Riservata</h1>
        <p className="mt-2 text-sm text-slate-500">Accedi per gestire lavori e novità del sito.</p>
        <div className="mt-8 space-y-5">
          <div>
            <Label htmlFor="admin-email" className={labelCls}>Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
              placeholder="admin@lattanzicostruzioni.it"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className={labelCls}>Password</Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>
          )}
          <Button
            data-testid="admin-login-button"
            type="submit"
            disabled={busy}
            className="w-full bg-gold text-white hover:bg-brand"
          >
            {busy ? "Accesso…" : "Accedi"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ProjectDialog({ open, onClose, kind, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [planFiles, setPlanFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...EMPTY, ...editing } : { ...EMPTY, kind });
      setFile(null);
      setPlanFiles([]);
    }
  }, [open, editing, kind]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadOne = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/upload", fd);
    return data.url;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let image = form.image;
      if (file) image = await uploadOne(file);
      if (!image) {
        toast.error("Carica una foto o incolla un URL immagine");
        setSaving(false);
        return;
      }
      const plans = [...(form.plans || [])];
      for (const pf of planFiles) plans.push(await uploadOne(pf));
      const payload = { ...form, kind, image, plans };
      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success("Elemento aggiornato");
      } else {
        await api.post("/projects", payload);
        toast.success("Elemento aggiunto");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removePlan = (idx) => setForm((f) => ({ ...f, plans: f.plans.filter((_, i) => i !== idx) }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-200 bg-white text-slate-900 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {editing ? "Modifica" : "Aggiungi"} {kind === "lavoro" ? "lavoro" : "novità"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className={labelCls}>Nome edificio *</Label>
            <Input data-testid="form-name-input" required value={form.name} onChange={set("name")} placeholder="Es. Residenza Via Galiano" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Foto principale * (quella che si vede nell'elenco)</Label>
            <Input
              data-testid="form-image-file"
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-2 border-slate-300 bg-white text-slate-600 file:mr-3 file:border-0 file:bg-gold file:px-3 file:py-1 file:text-xs file:text-white"
            />
            <Input
              data-testid="form-image-url"
              value={form.image}
              onChange={set("image")}
              placeholder="oppure incolla URL immagine (https://…)"
              className={inputCls}
            />
            {(file || form.image) && (
              <img
                src={file ? URL.createObjectURL(file) : imgUrl(form.image)}
                alt="Anteprima"
                className="mt-3 aspect-video w-full border border-slate-200 object-cover"
              />
            )}
          </div>
          {kind === "novita" && (
            <div>
              <Label className={labelCls}>Altre foto e piantine (visibili aprendo la scheda)</Label>
              <Input
                data-testid="form-plans-files"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp"
                onChange={(e) => setPlanFiles(Array.from(e.target.files || []))}
                className="mt-2 border-slate-300 bg-white text-slate-600 file:mr-3 file:border-0 file:bg-brand file:px-3 file:py-1 file:text-xs file:text-white"
              />
              {planFiles.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">{planFiles.length} nuove foto da caricare</p>
              )}
              {(form.plans || []).length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {form.plans.map((url, i) => (
                    <div key={i} className="group/plan relative">
                      <img src={imgUrl(url)} alt={`Foto ${i + 1}`} className="aspect-[4/3] w-full border border-slate-200 object-cover" />
                      <button
                        type="button"
                        data-testid={`form-plan-remove-${i}`}
                        onClick={() => removePlan(i)}
                        className="absolute right-1 top-1 grid h-6 w-6 place-items-center bg-gold text-white opacity-90 hover:opacity-100"
                        aria-label="Rimuovi foto"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Categoria</Label>
              <Input data-testid="form-category-input" value={form.category} onChange={set("category")} placeholder="Residenziale" className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>Luogo</Label>
              <Input data-testid="form-location-input" value={form.location} onChange={set("location")} placeholder="Fermo" className={inputCls} />
            </div>
          </div>
          {kind === "novita" && (
            <div>
              <Label className={labelCls}>Stato lavori</Label>
              <Input data-testid="form-status-input" value={form.status} onChange={set("status")} placeholder="Es. Lavori in Corso, Progetto Futuro" className={inputCls} />
            </div>
          )}
          {kind === "novita" && (
            <div>
              <Label className={labelCls}>Link vendita agenzia (facoltativo)</Label>
              <Input data-testid="form-sale-link-input" value={form.sale_link} onChange={set("sale_link")} placeholder="https://www.agenzia…/annuncio-palazzina" className={inputCls} />
              <p className="mt-1 text-xs text-slate-400">
                Se lo compili, chi apre la scheda vede il bottone verso l'annuncio dell'agenzia. Se lo lasci vuoto, vede i tuoi contatti.
              </p>
            </div>
          )}
          <div>
            <Label className={labelCls}>Descrizione</Label>
            <Textarea data-testid="form-description-input" value={form.description} onChange={set("description")} rows={3} placeholder="Qualche dettaglio sulla casa: camere, finiture, classe energetica…" className={inputCls} />
          </div>
          <Button data-testid="form-save-button" type="submit" disabled={saving} className="w-full bg-gold text-white hover:bg-brand">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProjectList({ kind }) {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api.get("/projects", { params: { kind } }).then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, [kind]);

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    try {
      await api.delete(`/projects/${item.id}`);
      toast.success("Elemento eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button
          data-testid="admin-add-project-button"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="gap-2 bg-gold text-white hover:bg-brand"
        >
          <Plus className="h-4 w-4" /> Aggiungi {kind === "lavoro" ? "lavoro" : "novità"}
        </Button>
      </div>
      {items.length === 0 ? (
        <p data-testid={`admin-empty-${kind}`} className="border border-dashed border-slate-300 py-16 text-center text-sm text-slate-400">
          Nessun elemento. Aggiungi il primo con il pulsante in alto.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`admin-item-${item.id}`}
              className="flex items-center gap-4 border border-slate-200 bg-white p-3"
            >
              <img src={imgUrl(item.image)} alt="" className="h-14 w-20 shrink-0 border border-slate-200 object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif text-lg text-slate-900">{item.name}</p>
                <p className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  {[item.category, item.location, item.status, (item.plans || []).length ? `${item.plans.length} foto extra` : ""].filter(Boolean).join(" · ")}
                </p>
              </div>
              <button
                data-testid={`admin-edit-${item.id}`}
                onClick={() => { setEditing(item); setDialogOpen(true); }}
                className="border border-slate-300 p-2 text-slate-500 transition-colors hover:border-gold hover:text-gold"
                aria-label="Modifica"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                data-testid={`admin-delete-${item.id}`}
                onClick={() => remove(item)}
                className="border border-slate-300 p-2 text-slate-500 transition-colors hover:border-gold hover:text-gold"
                aria-label="Elimina"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <ProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} kind={kind} editing={editing} onSaved={load} />
    </div>
  );
}

function SettingsForm() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => toast.error("Errore nel caricamento"));
  }, []);

  if (!form) return <p className="py-10 text-center text-sm text-slate-400">Caricamento…</p>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Contatti aggiornati su tutto il sito");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { k: "phone", label: "Telefono e Fax", tid: "settings-phone" },
    { k: "mobiles", label: "Numeri diretti (Marcello, Daniele, Marco)", tid: "settings-mobiles" },
    { k: "email", label: "E-mail", tid: "settings-email" },
    { k: "address", label: "Indirizzo sede", tid: "settings-address" },
    { k: "piva", label: "P.IVA e C.F.", tid: "settings-piva" },
    { k: "instagram", label: "Link profilo Instagram", tid: "settings-instagram" },
  ];

  return (
    <form data-testid="settings-form" onSubmit={submit} className="max-w-xl space-y-5">
      <p className="text-sm text-slate-500">
        Questi dati compaiono nella sezione contatti della home e nella striscia blu in fondo a ogni pagina.
      </p>
      {fields.map((f) => (
        <div key={f.k}>
          <Label className={labelCls}>{f.label}</Label>
          <Input data-testid={f.tid} value={form[f.k] || ""} onChange={set(f.k)} className={inputCls} />
        </div>
      ))}
      <Button data-testid="settings-save-button" type="submit" disabled={saving} className="bg-gold text-white hover:bg-brand">
        {saving ? "Salvataggio…" : "Salva contatti"}
      </Button>
    </form>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();

  if (user === null) {
    return <div className="grid min-h-[60vh] place-items-center pt-28 text-sm text-slate-400">Caricamento…</div>;
  }

  return (
    <main className="mx-auto min-h-screen max-w-5xl px-5 pb-24 pt-36 sm:px-8">
      {user === false ? (
        <LoginForm />
      ) : (
        <div data-testid="admin-dashboard">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Area Riservata</p>
              <h1 className="mt-2 font-serif text-3xl text-slate-900 sm:text-4xl">Gestione contenuti</h1>
              <p className="mt-1 text-sm text-slate-500">{user.email}</p>
            </div>
            <Button
              data-testid="admin-logout-button"
              variant="outline"
              onClick={logout}
              className="gap-2 border-slate-300 bg-transparent text-slate-600 hover:border-gold hover:text-gold"
            >
              <LogOut className="h-4 w-4" /> Esci
            </Button>
          </div>
          <Tabs defaultValue="lavoro" className="mt-10">
            <TabsList className="border border-slate-200 bg-obsidian">
              <TabsTrigger data-testid="admin-tab-lavori" value="lavoro" className="data-[state=active]:bg-brand data-[state=active]:text-white">
                Lavori già svolti
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-novita" value="novita" className="data-[state=active]:bg-gold data-[state=active]:text-white">
                Novità in cantiere
              </TabsTrigger>
              <TabsTrigger data-testid="admin-tab-contatti" value="contatti" className="data-[state=active]:bg-brand data-[state=active]:text-white">
                Contatti
              </TabsTrigger>
            </TabsList>
            <TabsContent value="lavoro" className="mt-8">
              <ProjectList kind="lavoro" />
            </TabsContent>
            <TabsContent value="novita" className="mt-8">
              <ProjectList kind="novita" />
            </TabsContent>
            <TabsContent value="contatti" className="mt-8">
              <SettingsForm />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </main>
  );
}
